import { SetDnsRecords, SetDnsStatus, SetInitialDnsLoad, SetLoadingDns, UpdateAmpDomain } from '../constants/mutations';
import {
  UpdateDomainList,
  PublishAmp,
  FetchLiveAmps,
  GetPublishedAmp,
  UpdateAmp,
  SaveDeploymentSettings,
  DeleteDeploymentSettings,
  VerifyDNS,
  VerifyInstallation,
  GetInstallationScript,
  FetchDraftAmps,
} from '../constants/actions';
import { GetCurrentAmp, GetCurrentDeployments } from '../constants/getters';
import { endpoints } from '~/services/amplify-api/endpoints';

const defaultState = () => {
  return {
    dnsStatus: '',
    loadingDns: false,
    initialDnsLoad: false,
    dnsRecords: [],
  };
};

export const state = defaultState;

export const mutations = {
  [SetDnsStatus](state, status) {
    state.dnsStatus = status;
  },

  [SetInitialDnsLoad](state, loading) {
    state.initialDnsLoad = loading;
  },

  [SetLoadingDns](state, loading) {
    state.loadingDns = loading;
  },

  [SetDnsRecords](state, records) {
    state.dnsRecords = records;
  },
};

export const actions = {
  [UpdateAmp]({ commit }, data) {
    commit(`ampStore/${UpdateAmp}`, data, { root: true });
  },

  [UpdateAmpDomain]({ dispatch, rootGetters }, domain) {
    const currentAmp = rootGetters[`ampStore/${GetCurrentAmp}`];
    const domains = [...currentAmp.domains];
    const index = domains.findIndex((dom) => dom.id === domain.id);
    domains[index] = domain;

    dispatch(UpdateAmp, { ...state.currentAmp, domains });
  },

  async [UpdateDomainList]({ dispatch, rootGetters }) {
    try {
      const currentAmp = rootGetters[`ampStore/${GetCurrentAmp}`];

      const { data: { data: domains = [] } = {} } = await this.$amplifyApi.get(
        endpoints.domain.getDomains(currentAmp.id)
      );
      const isLive = domains.some((domain) => domain.enabled && typeof domain.version === 'number');
      dispatch(UpdateAmp, { id: currentAmp.id, domains, metadata: { isLive } });
    } catch (error) {
      console.error(error);
    }
  },

  async [PublishAmp]({ dispatch }, { ampId, domainIds }) {
    try {
      const { status, data } = await this.$amplifyApi.post(endpoints.domain.publish(ampId), {
        domainIds,
      });

      if (status === 201 || status === 200) {
        await dispatch(UpdateDomainList);
        dispatch(`ampStore/${FetchLiveAmps}`, undefined, { root: true });
        dispatch(`ampStore/${FetchDraftAmps}`, undefined, { root: true });
      }

      return data || {};
    } catch (error) {
      const { response: { data: { statusCode, message } = {} } = {} } = error;

      if (statusCode === 400) {
        this.$notification.toast({
          type: 'warning',
          title: message,
          closable: true,
        });
      }

      console.error(error);
    }
  },

  async [GetPublishedAmp](_, ampId) {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.amp.published(ampId));
      return data;
    } catch (error) {
      console.error(error);
    }
  },

  async [SaveDeploymentSettings]({ rootGetters, dispatch }, settings) {
    try {
      let status, data;
      const currentAmp = rootGetters[`ampStore/${GetCurrentAmp}`];

      if (settings.id) {
        const { id, ...updates } = settings;

        ({ status, data } = await this.$amplifyApi.patch(
          endpoints.domain.updateDomain(currentAmp.id, settings.id),
          updates
        ));
      } else {
        ({ status, data } = await this.$amplifyApi.post(endpoints.domain.saveDomain(currentAmp.id), settings));
      }

      if (status === 201 || status === 200) {
        await dispatch(UpdateDomainList);
      }

      return data;
    } catch (error) {
      return error?.response?.data || error;
    }
  },

  async [DeleteDeploymentSettings]({ rootGetters, dispatch }, deploymentId) {
    try {
      const currentAmp = rootGetters[`ampStore/${GetCurrentAmp}`];
      const { status } = await this.$amplifyApi.delete(endpoints.domain.deleteDomain(currentAmp.id, deploymentId));

      if (status === 200) {
        await dispatch(UpdateDomainList);
      }
    } catch (error) {
      console.error(error);
    }
  },

  async [GetInstallationScript](_, ampId) {
    try {
      const { data } = await this.$amplifyApi.get(endpoints.amp.getInstallation(ampId));

      return data;
    } catch (error) {
      console.error(error);
    }
  },

  async [VerifyInstallation](_, { ampId, url }) {
    try {
      const {
        data: { status },
      } = await this.$amplifyApi.post(endpoints.amp.checkInstallation(ampId), { url });

      return status;
    } catch (error) {
      console.error(error);
    }
  },

  async [VerifyDNS](_, { ampId, domainId }) {
    try {
      const {
        data: { status },
      } = await this.$amplifyApi.post(endpoints.domain.checkDNS(ampId, domainId));

      return status;
    } catch (error) {
      console.error(error);
    }
  },
};

export const getters = {
  [GetCurrentDeployments](_state, _getters, rootState) {
    return rootState.ampStore.currentAmp?.domains || [];
  },
};
